import type { NewsItem } from "@hantavirus/shared";
import {
  Activity,
  Clock3,
  Crosshair,
  ExternalLink,
  Microscope,
  Radio,
  Table2,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import {
  OutbreakMap,
  type OutbreakMapRegion,
  type OutbreakMapShip,
} from "#/components/dashboard/OutbreakMap.js";
import {
  createT,
  formatDate,
  formatNumber,
  formatPercent,
  type TranslateFn,
} from "#/lib/i18n.js";
import type { TemplateLayoutProps } from "#/lib/templates/types.js";

type Tone = "danger" | "success" | "warning" | "neutral";

function toMapRegions(regions: TemplateLayoutProps["regions"]): OutbreakMapRegion[] {
  return regions.flatMap((region) => {
    if (!region.coordinates) return [];
    return [
      {
        id: region.id,
        name: region.name,
        dataText: region.dataText,
        caseCount: region.casesConfirmed ?? 0,
        coordinates: region.coordinates.coordinates as [number, number],
        risk:
          region.status === "cleared"
            ? "low"
            : region.status === "monitored"
              ? "moderate"
              : "high",
      },
    ];
  });
}

function toMapShip(ships: TemplateLayoutProps["ships"]): OutbreakMapShip | null {
  const vessel = ships.find((ship) => ship.trackingEnabled) ?? ships[0];
  if (!vessel) return null;
  return {
    mmsi: vessel.mmsi,
    name: vessel.name,
    trackCoordinates: [],
  };
}

function getCountryName(locale: string, code: string | null): string {
  if (!code) return "--";
  try {
    const dn = new Intl.DisplayNames([locale, "en"], { type: "region" });
    return dn.of(code.toUpperCase()) ?? code.toUpperCase();
  } catch {
    return code.toUpperCase();
  }
}

function sourceLabelFromUrl(sourceUrl: string | null | undefined): string {
  if (!sourceUrl) return "WHO/CDC";
  try {
    return new URL(sourceUrl).hostname.replace(/^www\./u, "").toUpperCase();
  } catch {
    return "SOURCE";
  }
}

function riskLabel(risk: TemplateLayoutProps["outbreak"]["whoClassification"], t: TranslateFn): string {
  return t(`risk.${risk}`);
}

function useUtcClock(): string {
  const [clock, setClock] = useState("--:--:-- UTC");

  useEffect(() => {
    const update = () => {
      setClock(
        new Intl.DateTimeFormat("en", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
          timeZone: "UTC",
        }).format(new Date()) + " UTC",
      );
    };
    update();
    const id = window.setInterval(update, 1000);
    return () => window.clearInterval(id);
  }, []);

  return clock;
}

function MetricBlock({
  label,
  value,
  tone,
  testId,
}: {
  label: string;
  value: string;
  tone: Tone;
  testId?: string;
}) {
  return (
    <article className="command-metric" data-tone={tone} data-testid={testId}>
      <span className="command-label">{label}</span>
      <strong>{value}</strong>
    </article>
  );
}

function StatusDot({ status }: { status: string }) {
  const normalized =
    status === "active" ? "confirmed" : status === "monitored" ? "monitoring" : "cleared";
  return (
    <span className="command-status" data-status={normalized}>
      <span aria-hidden="true" />
      {normalized.toUpperCase()}
    </span>
  );
}

function IntelFeed({
  items,
  locale,
  t,
}: {
  items: NewsItem[];
  locale: string;
  t: ReturnType<typeof createT>;
}) {
  const visibleItems = items.slice(0, 8);
  return (
    <aside id="news" className="command-panel command-feed" aria-label={t("news.ariaLabel")}>
      <header className="command-panel-header">
        <span>INTEL FEED</span>
        <Radio aria-hidden="true" size={14} />
      </header>
      <div className="command-feed-list">
        {visibleItems.length > 0 ? (
          visibleItems.map((item) => {
            const publishedAt =
              item.publishedAt instanceof Date
                ? item.publishedAt
                : new Date(item.publishedAt as unknown as string);
            return (
              <article key={item.id} className="command-feed-item">
                <div className="command-feed-meta">
                  <span>{item.source}</span>
                  <time dateTime={publishedAt.toISOString()}>
                    {formatDate(publishedAt, locale, { month: "short", day: "numeric" })}
                  </time>
                </div>
                <h2>{item.headline}</h2>
                {item.summary ? <p>{item.summary}</p> : null}
                <a href={item.sourceUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink aria-hidden="true" size={13} />
                  {t("news.readSource")}
                </a>
              </article>
            );
          })
        ) : (
          <p className="command-empty">{t("news.empty")}</p>
        )}
      </div>
    </aside>
  );
}

function RegionTable({
  regions,
  locale,
  t,
}: {
  regions: TemplateLayoutProps["regions"];
  locale: string;
  t: ReturnType<typeof createT>;
}) {
  const rows = [...regions]
    .sort((a, b) => (b.casesConfirmed ?? -1) - (a.casesConfirmed ?? -1))
    .slice(0, 12);

  return (
    <section id="regions" className="command-table-panel" data-testid="outbreak-grid">
      <div className="command-tabs" role="tablist" aria-label="Dashboard data views">
        <button type="button" role="tab" aria-selected="true">
          <Table2 aria-hidden="true" size={14} />
          COUNTRY TABLE
        </button>
        <button type="button" role="tab" aria-selected="false" disabled id="timeline">
          <Clock3 aria-hidden="true" size={14} />
          TIMELINE
        </button>
        <button type="button" role="tab" aria-selected="false" disabled>
          <Microscope aria-hidden="true" size={14} />
          EPIDEMIOLOGY
        </button>
      </div>
      <div className="command-table-scroll">
        <table>
          <thead>
            <tr>
              <th>{t("grid.column.country")}</th>
              <th>{t("grid.column.region")}</th>
              <th>{t("grid.column.confirmed")}</th>
              <th>DEATHS</th>
              <th>{t("grid.column.cfr")}</th>
              <th>STATUS</th>
              <th>SOURCE</th>
            </tr>
          </thead>
          <tbody>
            {rows.length > 0 ? (
              rows.map((region) => (
                <tr key={region.id}>
                  <td>{getCountryName(locale, region.countryCode)}</td>
                  <td>{region.name}</td>
                  <td className="command-number command-number-success">
                    {region.casesConfirmed == null
                      ? "--"
                      : formatNumber(region.casesConfirmed, locale)}
                  </td>
                  <td className="command-number command-number-danger">
                    {region.deaths == null ? "--" : formatNumber(region.deaths, locale)}
                  </td>
                  <td className="command-number command-number-warning">
                    {region.caseFatalityPct == null
                      ? "--"
                      : `${formatNumber(region.caseFatalityPct, locale)}%`}
                  </td>
                  <td>
                    <StatusDot status={region.status} />
                  </td>
                  <td>{sourceLabelFromUrl(region.sourceUrl)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7}>{t("grid.empty")}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function CommandCenterHome(props: TemplateLayoutProps) {
  const {
    outbreak,
    outbreaks,
    caseCounts,
    ticker,
    news,
    regions,
    ships,
    pathogen,
    outbreakSources,
    locale,
  } = props;
  const t = createT(locale, "landing");
  const pathogenT = createT(locale, "pathogen");
  const utcClock = useUtcClock();
  const activeOutbreaks = outbreaks.filter(
    (item) => item.status === "active" || item.status === "emerging",
  ).length;
  const monitoredRegions = regions.filter((region) => region.status !== "cleared").length;
  const mapRegions = useMemo(() => toMapRegions(regions), [regions]);
  const mapShip = useMemo(() => toMapShip(ships), [ships]);
  const sourceLine = [
    ...new Set([
      ...outbreakSources.slice(0, 4).map((source) => source.name),
      ...caseCounts.sources.slice(0, 2).map((source) => source.label),
      ...ticker.slice(0, 2).map((item) => item.source),
    ]),
  ]
    .slice(0, 6)
    .join(" / ");
  const tickerItems =
    ticker.length > 0
      ? ticker.map((item) => item.headline)
      : [outbreak.summary, t("ticker.empty")];
  const mapLabels = regions.slice(0, 3).map((region) => region.name.toUpperCase());

  return (
    <main
      id="main-content"
      className="command-center"
      data-template-layout="bento-console"
      data-testid="hero"
    >
      <header className="command-statusbar">
        <a className="command-brand" href="/" aria-label={t("chrome.brandAria")}>
          <Crosshair aria-hidden="true" size={18} />
          <span>HANTAVIRUS TRACKER</span>
        </a>
        <div className="command-ticker" role="marquee" aria-label={t("ticker.label")}>
          <div className="command-ticker-track">
            {[...tickerItems, ...tickerItems].map((headline, index) => (
              <span key={`${headline}-${index}`}>{headline}</span>
            ))}
          </div>
        </div>
        <div className="command-status-items">
          <time data-snapshot="hide">{utcClock}</time>
          <span className="command-live">
            <span aria-hidden="true" />
            {t("ticker.live")}
          </span>
          <span className="who-pill" data-risk={outbreak.whoClassification}>
            WHO {riskLabel(outbreak.whoClassification, t)}
          </span>
        </div>
      </header>

      <div className="command-meta">// SOURCE: {sourceLine || "WHO / CDC / PAHO / PROMED"}</div>

      <section className="command-body" aria-label="Outbreak monitoring command center">
        <aside className="command-panel command-left">
          <div className="command-metrics">
            <MetricBlock
              label="CONFIRMED DEATHS"
              value={formatNumber(caseCounts.deathsTotal, locale)}
              tone="danger"
              testId="hero-kpi-deaths"
            />
            <MetricBlock
              label="REPORTED CASES"
              value={formatNumber(caseCounts.confirmedTotal, locale)}
              tone="success"
              testId="hero-kpi-confirmed"
            />
            <MetricBlock
              label="ACTIVE OUTBREAKS"
              value={formatNumber(activeOutbreaks, locale)}
              tone="warning"
            />
            <MetricBlock
              label="REGIONS MONITORED"
              value={formatNumber(monitoredRegions, locale)}
              tone="neutral"
            />
          </div>

          <article className="command-cfr" data-testid="hero-kpi-cfr">
            <span className="command-label">{t("kpi.cfr")}</span>
            <strong>{formatPercent(caseCounts.cfrPercent, locale)}</strong>
          </article>

          <dl className="command-readout">
            <div>
              <dt>STRAIN</dt>
              <dd>{pathogen?.name ?? outbreak.name}</dd>
            </div>
            <div>
              <dt>SYNDROME</dt>
              <dd>{outbreak.status.toUpperCase()}</dd>
            </div>
            <div>
              <dt>{pathogenT("vector").toUpperCase()}</dt>
              <dd>{pathogen?.vector ?? "--"}</dd>
            </div>
            <div>
              <dt>{pathogenT("h2h").toUpperCase()}</dt>
              <dd>{pathogen?.h2hTransmission ?? "--"}</dd>
            </div>
            <div>
              <dt>{pathogenT("incubation").toUpperCase()}</dt>
              <dd>
                {pathogen
                  ? pathogenT("incubationRange", {
                      min: pathogen.incubationMinDays,
                      max: pathogen.incubationMaxDays,
                    })
                  : "--"}
              </dd>
            </div>
          </dl>
        </aside>

        <section id="map" className="command-panel command-map" aria-label={t("section.map")}>
          <div className="command-map-header">
            <div>
              <span className="command-label">PRIMARY SURVEILLANCE MAP</span>
              <h1>{outbreak.name}</h1>
            </div>
            <span className="command-map-count">
              <Activity aria-hidden="true" size={14} />
              {formatNumber(mapRegions.length, locale)} NODES
            </span>
          </div>
          <div className="command-map-frame">
            <OutbreakMap regions={mapRegions} ship={mapShip} locale={locale} t={t} />
            <div className="command-map-labels" aria-hidden="true">
              {mapLabels.map((label) => (
                <span key={label}>{label}</span>
              ))}
            </div>
            <div className="command-vessel">
              <span>{mapShip?.name ?? "VESSEL TELEMETRY"}</span>
              <strong>{mapShip ? `MMSI ${mapShip.mmsi}` : "NO ACTIVE AIS TARGET"}</strong>
              <small>
                {ships.length > 0
                  ? `${formatNumber(ships.length, locale)} tracked vessel signals`
                  : "Awaiting feed"}
              </small>
            </div>
          </div>
        </section>

        <IntelFeed items={news.items as NewsItem[]} locale={locale} t={t} />
      </section>

      <RegionTable regions={regions} locale={locale} t={t} />
    </main>
  );
}
